import { createApi } from '@reduxjs/toolkit/query/react';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice';
import { validateBlobResponse } from '../utils/helpers';
import BaseQuery from './base.api';
import { handleFilterParams } from './filterParamUtils';
import type { Contract } from './models/contract.model';
import type { FilterSearchRequestParams } from './models/filters.model';
import type { PaginatedResults } from './models/paginatedResults.model';

export interface ContractCreate {
	contract_no: string;
	status: 'Active' | 'Inactive' | 'Pending' | 'Expired';
	start_date: string;
	end_date: string;
	client_name?: string;
	value?: number;
}

export interface ContractUpdate extends Partial<ContractCreate> {
	id: number;
}

// Define the API slice
export const contractsApi = createApi({
	reducerPath: 'contractsApi',
	baseQuery: BaseQuery('/v1/contracts'),
	tagTypes: ['Contract', 'Export'],
	endpoints: (builder) => ({
		// Get all contracts
		getContracts: builder.query<
			PaginatedResults<Contract>,
			{
				accountId: number;
				sourceId: number;
				accountTypeCode: string;
				filters?: Record<string, any>;
				pageSize: number;
				currentPage: number;
				sortDirection: string;
				sortField: string;
			}
		>({
			query: ({
				accountId,
				sourceId,
				accountTypeCode,
				filters,
				pageSize,
				currentPage,
				sortDirection,
				sortField,
			}) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				// TODO: When auth in place, remove and get customer id on backend
				return `?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection}&sortField=${sortField}${paramString}`;
			}, // Note: your endpoint is still /api/contacts
			providesTags: ['Contract'],
		}),

		// Get single contract
		getContract: builder.query<Contract, number>({
			query: (id) => `/${id}`,
			providesTags: (_result, _error, id) => [{ type: 'Contract', id }],
		}),

		bulkUpdateContracts: builder.mutation<
			Contract[],
			{ contractIds: string[]; updates: Partial<Contract> }
		>({
			query: ({ contractIds, updates }) => ({
				url: '/bulk-update',
				method: 'POST',
				body: { contractIds, updates },
			}),
			invalidatesTags: ['Contract'],
		}),

		getContractsByUser: builder.query<Contract[], number>({
			query: (userId) => `/user/${userId}`,
			providesTags: ['Contract'],
		}),

		exportContracts: builder.query<Blob, FilterSearchRequestParams>({
			async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { accountId, accountTypeCode, sourceId, filters, selectedRows = [] } = arg;
				const params = new URLSearchParams();

				// A selection takes precedence over the active filters.
				if (selectedRows.length > 0) {
					const contractIds = selectedRows
						.map((row) => row.id)
						.filter((id) => id !== undefined && id !== null);
					params.append('contractIds', contractIds.join(','));
				} else if (filters) {
					handleFilterParams(filters, params);
				}
				const paramString = params.size > 0 ? `&${params.toString()}` : '';

				const response = await fetchWithBQ({
					url: `export?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`,
					method: 'GET',
					responseHandler: (r: Response) => r.blob(),
				});

				return validateBlobResponse(response);
			},
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			keepUnusedDataFor: 5,
			providesTags: ['Export'],
		}),
	}),
});

// Export hooks for usage in functional components
export const {
	useGetContractsQuery,
	useGetContractQuery,
	useBulkUpdateContractsMutation,
	useGetContractsByUserQuery,
	useLazyExportContractsQuery,
	useLazyGetContractsQuery,
} = contractsApi;
